// src/components/common/CustomSelect.tsx
import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from 'react';
import ReactDOM from 'react-dom';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  ClickAwayListener,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { KeyboardArrowDown } from '@mui/icons-material';
import { ErrorOutline as ErrorOutlineIcon } from '@mui/icons-material';
import {
  getInputChromeStyles,
  getInputFocusStyles,
} from '../forms/shared/InputChromeStyles';
import {
  getAccessibilityInputHover,
  getAccessibilityListItemHover,
  InputAccessibilityHoverProps,
  ListItemAccessibilityHoverProps,
} from '../forms/shared/AccessibilityHoverStyles';
import { useAccessibility } from '../../contexts/AccessibilityContext';

interface CustomSelectProps extends InputAccessibilityHoverProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  // Overrides the computed accessible name without affecting whether the
  // visible floating label renders (that stays gated on `label` alone).
  // For callers that already render their own external label and don't
  // want CustomSelect's built-in one duplicated.
  ariaLabel?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  children: React.ReactNode;
  placeholder?: string;
  required?: boolean;
  loading?: boolean;
  maxWidth?: string | number;
  width?: string | number;
  minWidth?: string | number;
  // Forwarded to the trigger button. Lets parents implement keyboard
  // navigation (e.g. Enter-as-Tab). Fires for Enter when the dropdown is
  // closed (advance) and after Enter commits a selection from an open
  // dropdown (advance). Other keys consumed internally do not forward.
  onKeyDown?: (event: React.KeyboardEvent) => void;
}

interface CustomSelectMenuItemProps {
  value: string;
  children: React.ReactNode;
  disabled?: boolean;
}

const SelectContainer = styled(FormControl, {
  shouldForwardProp: (prop) =>
    prop !== 'maxWidth' && prop !== 'width' && prop !== 'minWidth',
})<{
  maxWidth?: string | number;
  width?: string | number;
  minWidth?: string | number;
}>(({ theme, maxWidth, width, minWidth }) => ({
  position: 'relative',
  width: width || '100%',
  maxWidth: maxWidth,
  minWidth: minWidth,
}));

const SelectButton = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'error' &&
    prop !== 'disabled' &&
    prop !== 'open' &&
    prop !== 'hasValue' &&
    ![
      'accessibilityMode',
      'accessibilityHoverDisabled',
      'accessibilityHoverBorder',
      'accessibilityHoverBorderColor',
      'accessibilityHoverBackgroundColor',
      'accessibilityHoverColor',
      'accessibilityHoverGlow',
    ].includes(prop as string),
})<
  {
    error?: boolean;
    disabled?: boolean;
    open?: boolean;
    hasValue?: boolean;
  } & InputAccessibilityHoverProps & {
      accessibilityMode?: boolean;
    }
>(({ theme, error, disabled, open, ...accessibilityProps }) => {
  const chrome = getInputChromeStyles(theme, { error });
  return {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0rem',
    fontSize: '2rem',
    fontWeight: 600,
    ...chrome,
    boxShadow: disabled ? 'none' : chrome.boxShadow,
    backgroundColor: disabled
      ? theme.palette.mode === 'dark'
        ? theme.palette.grey[800]
        : theme.palette.grey[100]
      : theme.palette.mode === 'dark'
        ? theme.palette.grey[900]
        : theme.palette.grey[50],
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.3s ease',
    opacity: disabled ? 0.6 : 1,
    '&:hover':
      disabled || error
        ? {}
        : getAccessibilityInputHover(
            theme,
            Boolean(accessibilityProps.accessibilityMode),
            accessibilityProps
          ),
    '&:focus':
      disabled || error
        ? {}
        : {
            outline: 'none',
            ...getInputFocusStyles(theme, chrome.boxShadow),
            ...getAccessibilityInputHover(
              theme,
              Boolean(accessibilityProps.accessibilityMode),
              accessibilityProps
            ),
          },
  };
});

const SelectText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'hasValue',
})<{ hasValue?: boolean }>(({ theme, hasValue }) => ({
  fontSize: '2rem',
  fontWeight: 600,
  color: hasValue ? theme.palette.text.primary : theme.palette.text.secondary,
  userSelect: 'none',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flex: 1,
  textAlign: 'left',
  paddingLeft: '0.25rem',
}));

const ArrowIcon = styled(KeyboardArrowDown, {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open?: boolean }>(({ theme, open }) => ({
  fontSize: '3rem',
  transition: 'transform 0.3s ease',
  transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.primary.light
      : theme.palette.primary.main,
  flexShrink: 0,
}));

const StyledInputLabel = styled(InputLabel, {
  shouldForwardProp: (prop) => prop !== 'shrink' && prop !== 'hasValue',
})<{ shrink?: boolean; hasValue?: boolean }>(({ theme, shrink, hasValue }) => ({
  fontSize: '1.2rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  position: 'absolute',
  top: shrink || hasValue ? '-9px' : '16px',
  left: '14px',
  backgroundColor: theme.palette.background.paper,
  padding: shrink || hasValue ? '0 4px' : '0',
  zIndex: 1,
  transition: 'all 0.3s ease',
  transform: shrink || hasValue ? 'scale(0.85)' : 'scale(1)',
  transformOrigin: 'left top',
  pointerEvents: 'none',
  '&.Mui-focused': {
    color: theme.palette.primary.main,
  },
  '&.Mui-error': {
    color: theme.palette.error.main,
  },
}));

// Position state for the portal dropdown
interface DropdownPosition {
  top: number;
  left: number;
  width: number;
  openUpward: boolean;
}

// The dropdown's own scrollable cap - also used as the worst-case height
// estimate for the flip decision below, before the dropdown has rendered
// once to measure its real (possibly shorter) height.
const DROPDOWN_MAX_HEIGHT = 300;
const DROPDOWN_VIEWPORT_MARGIN = 8;

const CustomSelect = React.forwardRef<HTMLDivElement, CustomSelectProps>(
  (
    {
      value,
      onChange,
      label,
      ariaLabel,
      disabled = false,
      error = false,
      helperText,
      children,
      placeholder = 'Please select...',
      maxWidth,
      width,
      minWidth,
      onKeyDown,
      accessibilityHoverDisabled,
      accessibilityHoverBorder,
      accessibilityHoverBorderColor,
      accessibilityHoverBackgroundColor,
      accessibilityHoverColor,
      accessibilityHoverGlow,
    },
    ref
  ) => {
    const { accessibilityMode } = useAccessibility();
    const accessibilityHoverProps = {
      accessibilityHoverDisabled,
      accessibilityHoverBorder,
      accessibilityHoverBorderColor,
      accessibilityHoverBackgroundColor,
      accessibilityHoverColor,
      accessibilityHoverGlow,
    };
    const [open, setOpen] = useState(false);
    const [highlightedIdx, setHighlightedIdx] = useState(-1);
    const [dropdownPos, setDropdownPos] = useState<DropdownPosition>({
      top: 0,
      left: 0,
      width: 0,
      openUpward: false,
    });
    const containerRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    // Refs to each rendered option DOM node, for scrollIntoView on highlight.
    const optionElsRef = useRef<Array<HTMLElement | null>>([]);
    // Set on mousedown so the focus that follows a click does not auto-open
    // the dropdown (the click itself already toggles via handleToggle).
    const skipNextFocusOpenRef = useRef(false);
    const theme = useTheme();

    // Forward the trigger button to the parent ref so it can be focused.
    React.useImperativeHandle(ref, () => buttonRef.current as HTMLDivElement);

    const updateDropdownPosition = useCallback(() => {
      if (!buttonRef.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const margin = DROPDOWN_VIEWPORT_MARGIN;

      // Once the dropdown has rendered at least once this open, its own
      // scrollHeight (capped by its maxHeight) is the real height to plan
      // around; before that first measurement, DROPDOWN_MAX_HEIGHT is the
      // safe worst case - a shorter list only ever needs less room than
      // this estimate assumes, never more.
      const estimatedHeight = Math.min(
        dropdownRef.current?.scrollHeight ?? DROPDOWN_MAX_HEIGHT,
        DROPDOWN_MAX_HEIGHT
      );

      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUpward =
        spaceBelow < estimatedHeight && spaceAbove > spaceBelow;

      const top = openUpward
        ? Math.max(margin, rect.top - estimatedHeight)
        : rect.bottom;

      // Clamp horizontally so a trigger near the right edge (or one wider
      // than the viewport) never pushes the dropdown off-screen.
      const left = Math.max(
        margin,
        Math.min(rect.left, viewportWidth - rect.width - margin)
      );

      setDropdownPos({ top, left, width: rect.width, openUpward });
    }, []);

    // Recalculate position on open, scroll, and resize. useLayoutEffect
    // (not useEffect) so the first measurement - including the flip
    // decision, which depends on the dropdown's own just-rendered height -
    // happens before paint, rather than visibly jumping into place a
    // frame after it first appears.
    useLayoutEffect(() => {
      if (!open) return;
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
      return () => {
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }, [open, updateDropdownPosition]);

    const options =
      React.Children.map(children, (child) => {
        if (React.isValidElement<CustomSelectMenuItemProps>(child)) {
          return {
            value: child.props.value,
            label: child.props.children,
            disabled: child.props.disabled || false,
          };
        }
        return null;
      })?.filter(Boolean) || [];

    const selectedOption = options?.find((option) => option?.value === value);
    const hasValue = Boolean(value && value !== '');

    // When no `label` prop is given (most callers), fall back to the
    // placeholder as the implied field name, and finally to a hardcoded
    // string if the placeholder itself is ever empty - so the aria-label
    // never renders a literal "undefined:" or ": " prefix. When no value is
    // selected and there's no real label, the prefix is dropped entirely
    // rather than repeating the placeholder against itself (e.g. "Type"
    // instead of "Type: Type"). `ariaLabel` takes priority when given,
    // letting a caller with its own external visible label (so it doesn't
    // want CustomSelect's built-in one duplicated) still supply an
    // accurate accessible name instead of falling back to the placeholder.
    const accessibleLabel = ariaLabel || label || placeholder || 'Select';

    // Indices of enabled options, used for arrow-key navigation that skips
    // disabled rows and wraps at the ends.
    const enabledIndices = options
      .map((o, i) => (o?.disabled ? -1 : i))
      .filter((i): i is number => i !== -1);

    // Initialise the keyboard highlight when the dropdown opens.
    // Lands on the current value (if any) or the first enabled option.
    useEffect(() => {
      if (!open) {
        setHighlightedIdx(-1);
        return;
      }
      const valueIdx = options.findIndex((o) => o?.value === value);
      if (valueIdx >= 0 && !options[valueIdx]?.disabled) {
        setHighlightedIdx(valueIdx);
      } else {
        setHighlightedIdx(enabledIndices[0] ?? -1);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Keep the highlighted option visible inside the scrollable dropdown.
    useEffect(() => {
      if (!open || highlightedIdx < 0) return;
      const el = optionElsRef.current[highlightedIdx];
      if (el) el.scrollIntoView({ block: 'nearest' });
    }, [highlightedIdx, open]);

    const handleToggle = () => {
      if (!disabled) setOpen(!open);
    };

    const handleOptionSelect = (optionValue: string) => {
      onChange(optionValue);
      setOpen(false);
    };

    const handleClickAway = () => {
      setOpen(false);
    };

    const handleMouseDown = () => {
      // Suppress the auto-open that would otherwise happen when the trigger
      // gains focus from the click. handleToggle (via onClick) handles it.
      skipNextFocusOpenRef.current = true;
      window.setTimeout(() => {
        skipNextFocusOpenRef.current = false;
      }, 0);
    };

    const handleFocus = () => {
      if (disabled) return;
      if (skipNextFocusOpenRef.current) return;
      if (!open) setOpen(true);
    };

    const moveHighlight = (dir: 1 | -1) => {
      if (enabledIndices.length === 0) return;
      setHighlightedIdx((curr) => {
        const pos = enabledIndices.indexOf(curr);
        if (pos === -1) {
          return dir === 1
            ? enabledIndices[0]
            : enabledIndices[enabledIndices.length - 1];
        }
        const next =
          (pos + dir + enabledIndices.length) % enabledIndices.length;
        return enabledIndices[next];
      });
    };

    const commitAndAdvance = (event: React.KeyboardEvent) => {
      if (highlightedIdx >= 0) {
        const opt = options[highlightedIdx];
        if (opt && !opt.disabled) {
          onChange(opt.value);
        }
      }
      setOpen(false);
      // Defer the chain advance so React commits any state change (e.g. a
      // dependent control becoming enabled) before the parent walks forward.
      const evt = event;
      window.setTimeout(() => onKeyDown?.(evt), 0);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (disabled) return;

      if (event.key === 'Escape') {
        if (open) {
          event.preventDefault();
          setOpen(false);
        }
        return;
      }

      if (open) {
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            moveHighlight(1);
            return;
          case 'ArrowUp':
            event.preventDefault();
            moveHighlight(-1);
            return;
          case 'Home':
            event.preventDefault();
            if (enabledIndices.length > 0) setHighlightedIdx(enabledIndices[0]);
            return;
          case 'End':
            event.preventDefault();
            if (enabledIndices.length > 0) {
              setHighlightedIdx(enabledIndices[enabledIndices.length - 1]);
            }
            return;
          case 'Tab':
            // Close on Tab; let the browser advance focus naturally.
            setOpen(false);
            return;
          case 'Enter':
          case ' ':
            event.preventDefault();
            commitAndAdvance(event);
            return;
        }
        return;
      }

      // Closed-state key handling.
      if (
        event.key === ' ' ||
        event.key === 'ArrowDown' ||
        event.key === 'ArrowUp'
      ) {
        event.preventDefault();
        setOpen(true);
        return;
      }
      // Enter (and any other key) forwards to the parent so the form chain
      // can advance.
      onKeyDown?.(event);
    };

    // Portal-rendered dropdown
    const dropdown = open
      ? ReactDOM.createPortal(
          <Box
            ref={dropdownRef}
            role="listbox"
            sx={{
              position: 'fixed',
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
              // ModalBase's own StyledDialog is explicitly raised to
              // zIndex: 2100 (to beat StickyFooter's 2000) - this portaled
              // dropdown needs to clear that too, or it renders beneath
              // any ModalBase dialog it's opened from (e.g. AddPaymentModal).
              // Kept comfortably below NotificationToast's 9999.
              zIndex: 2200,
              backgroundColor: theme.palette.background.paper,
              border: `2px solid ${theme.palette.primary.main}`,
              // When there isn't room below the trigger, the dropdown
              // flips to open upward instead - so the border/radius side
              // that touches the trigger (and is therefore omitted to
              // read as one continuous control) flips with it.
              ...(dropdownPos.openUpward
                ? { borderBottom: 'none', borderRadius: '4px 4px 0 0' }
                : { borderTop: 'none', borderRadius: '0 0 4px 4px' }),
              maxHeight: `${DROPDOWN_MAX_HEIGHT}px`,
              overflowX: 'hidden',
              overflowY: 'auto',
              boxShadow: theme.shadows[8],
            }}
          >
            {options?.map((option, index) => (
              <Box
                key={option?.value || index}
                id={`custom-select-option-${index}`}
                ref={(el: HTMLElement | null) => {
                  optionElsRef.current[index] = el;
                }}
                onClick={() =>
                  !option?.disabled && handleOptionSelect(option?.value || '')
                }
                onMouseEnter={() => {
                  if (!option?.disabled) setHighlightedIdx(index);
                }}
                tabIndex={-1}
                role="option"
                aria-selected={option?.value === value}
                sx={{
                  padding: '12px 16px',
                  fontSize: '2rem',
                  fontWeight: 600,
                  cursor: option?.disabled ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  color: option?.disabled
                    ? theme.palette.text.disabled
                    : theme.palette.text.primary,
                  opacity: option?.disabled ? 0.5 : 1,
                  fontStyle: option?.disabled ? 'italic' : 'normal',
                  pointerEvents: option?.disabled ? 'none' : 'auto',
                  // Keyboard highlight (tinted background + outline).
                  ...(index === highlightedIdx && !option?.disabled
                    ? {
                        backgroundColor: `${theme.palette.primary.main}25`,
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: '-2px',
                      }
                    : {}),
                  '&:hover': !option?.disabled
                    ? {
                        backgroundColor: `${theme.palette.primary.main}15`,
                        color: theme.palette.text.primary,
                        letterSpacing: '1px',
                        transform: 'translateX(4px)',
                        ...getAccessibilityListItemHover(
                          theme,
                          accessibilityMode,
                          accessibilityHoverProps
                        ),
                      }
                    : {},
                  '&:last-child': {
                    borderBottom: 'none',
                  },
                }}
              >
                {option?.label}
              </Box>
            ))}
          </Box>,
          document.body
        )
      : null;

    return (
      <ClickAwayListener onClickAway={handleClickAway}>
        <SelectContainer
          ref={containerRef}
          error={error}
          disabled={disabled}
          maxWidth={maxWidth}
          width={width}
          minWidth={minWidth}
        >
          {label && (
            <StyledInputLabel
              shrink={open}
              hasValue={hasValue}
              error={error}
              htmlFor={label ? `custom-select-${label}` : 'custom-select'}
            >
              {label}
            </StyledInputLabel>
          )}

          <SelectButton
            ref={buttonRef}
            id={label ? `custom-select-${label}` : 'custom-select'}
            error={error}
            disabled={disabled}
            open={open}
            hasValue={hasValue}
            accessibilityMode={accessibilityMode}
            {...accessibilityHoverProps}
            onClick={handleToggle}
            onMouseDown={handleMouseDown}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            tabIndex={disabled ? -1 : 0}
            role="combobox"
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-disabled={disabled || undefined}
            aria-activedescendant={
              open && highlightedIdx >= 0
                ? `custom-select-option-${highlightedIdx}`
                : undefined
            }
            aria-label={
              hasValue
                ? `${accessibleLabel}: ${selectedOption?.label}`
                : label
                  ? `${accessibleLabel}: ${placeholder}`
                  : accessibleLabel
            }
          >
            <SelectText hasValue={hasValue}>
              {selectedOption?.label || placeholder}
            </SelectText>
            <ArrowIcon open={open} />
          </SelectButton>

          {dropdown}

          {helperText && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                mt: '0.5rem',
                ml: '0.5rem',
              }}
            >
              {error && (
                <Box
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '2rem',
                      height: '2rem',
                      borderRadius: '50%',
                      border: `3px solid ${
                        theme.palette.mode === 'dark'
                          ? theme.palette.error.light
                          : theme.palette.error.main
                      }`,
                      backgroundColor: 'transparent',
                      opacity: 0.8,
                      animation: 'radiate 1s ease-out infinite',
                    },
                    '@keyframes radiate': {
                      '0%': {
                        transform: 'translate(-50%, -50%) scale(0.5)',
                        opacity: 0.8,
                      },
                      '100%': {
                        transform: 'translate(-50%, -50%) scale(2.5)',
                        opacity: 0,
                      },
                    },
                  }}
                >
                  <ErrorOutlineIcon
                    sx={{
                      fontSize: '2rem',
                      color:
                        theme.palette.mode === 'dark'
                          ? theme.palette.error.light
                          : theme.palette.error.main,
                      position: 'relative',
                      zIndex: 1,
                    }}
                  />
                </Box>
              )}
              <Typography
                sx={{
                  fontSize: error ? '2rem' : '1.8rem',
                  fontWeight: error ? 700 : 500,
                  m: 0,
                  color:
                    theme.palette.mode === 'dark'
                      ? error
                        ? theme.palette.error.light
                        : theme.palette.grey[400]
                      : error
                        ? theme.palette.error.main
                        : theme.palette.text.secondary,
                }}
              >
                {helperText}
              </Typography>
            </Box>
          )}
        </SelectContainer>
      </ClickAwayListener>
    );
  }
);

CustomSelect.displayName = 'CustomSelect';

export default CustomSelect;
